import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, ChatCircle, MapPin, ArrowSquareOut, AirplaneTilt, Buildings, Camera, BookmarkSimple } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";
import AuthImage from "./AuthImage";
import AddToCollectionMenu from "./AddToCollectionMenu";

const CAT_META = {
    flight: { Icon: AirplaneTilt, label: "Flight" },
    hotel: { Icon: Buildings, label: "Hotel" },
    destination: { Icon: MapPin, label: "Destination" },
    activity: { Icon: Camera, label: "Activity" },
};

function formatRelative(d) {
    const date = new Date(d);
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
}

export default function PostCard({ post, onUpdate, index = 0 }) {
    const meta = CAT_META[post.category] || CAT_META.destination;
    const Icon = meta.Icon;

    const [busy, setBusy] = React.useState(false);
    const [bmBusy, setBmBusy] = React.useState(false);
    const handleUpvote = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/posts/${post.id}/upvote`);
            onUpdate?.({ ...post, upvotes: data.upvotes, has_upvoted: data.upvoted });
        } catch (_) {
            toast.error("Could not upvote");
        } finally {
            setBusy(false);
        }
    };

    const handleBookmark = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (bmBusy) return;
        setBmBusy(true);
        try {
            const { data } = await api.post(`/posts/${post.id}/bookmark`);
            onUpdate?.({ ...post, is_bookmarked: data.bookmarked });
            toast.success(data.bookmarked ? "Saved to your trip plan" : "Removed from saved");
        } catch (_) {
            toast.error("Could not save");
        } finally {
            setBmBusy(false);
        }
    };

    const initials = (post.user_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

    return (
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            data-testid={`post-card-${post.id}`}
            className="bg-white border border-stone-200 rounded-xl p-6 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(153,0,0,0.08)] hover:border-[#990000]/20 transition-all duration-300 group"
        >
            <div className="flex gap-4">
                {/* Vote column */}
                <div className="flex flex-col items-center gap-1 pt-1">
                    <button
                        data-testid={`post-upvote-btn-${post.id}`}
                        onClick={handleUpvote}
                        disabled={busy}
                        aria-label="Upvote"
                        className={`w-9 h-9 rounded-md flex items-center justify-center border transition-all ${
                            post.has_upvoted
                                ? "bg-[#990000] border-[#990000] text-[#FFCC00]"
                                : "bg-white border-stone-200 text-stone-500 hover:border-[#990000] hover:text-[#990000]"
                        }`}
                    >
                        <ArrowUp size={18} weight={post.has_upvoted ? "fill" : "bold"} />
                    </button>
                    <span className={`text-sm font-semibold tabular-nums ${post.has_upvoted ? "text-[#990000]" : "text-stone-700"}`}>
                        {post.upvotes}
                    </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FFF4CC] border border-[#FFE680] text-[#5C4400] text-[11px] font-bold uppercase tracking-wider">
                            <Icon size={12} weight="fill" />
                            {meta.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-stone-500">
                            <MapPin size={12} /> {post.city}
                        </span>
                        <span className="text-xs text-stone-400">·</span>
                        <span className="text-xs text-stone-500">{formatRelative(post.created_at)}</span>
                    </div>

                    <Link to={`/posts/${post.id}`} data-testid={`post-title-link-${post.id}`}>
                        <h3 className="font-heading text-lg sm:text-xl font-semibold text-[#0A0A0A] leading-snug group-hover:text-[#990000] transition-colors mb-2">
                            {post.title}
                        </h3>
                    </Link>

                    {post.image_url && (
                        <Link to={`/posts/${post.id}`}>
                            <AuthImage
                                src={post.image_url}
                                alt={post.title}
                                className="w-full h-56 object-cover rounded-lg border border-stone-200 mb-3"
                                testId={`post-image-${post.id}`}
                            />
                        </Link>
                    )}

                    <p className="text-sm text-stone-600 leading-relaxed line-clamp-3 mb-3">
                        {post.description}
                    </p>

                    {post.url && (
                        <a
                            data-testid={`post-url-link-${post.id}`}
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#990000] hover:underline mb-3 max-w-full overflow-hidden"
                        >
                            <ArrowSquareOut size={12} weight="bold" />
                            <span className="truncate">{post.url.replace(/^https?:\/\//, "").slice(0, 50)}</span>
                        </a>
                    )}

                    {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            {post.tags.slice(0, 5).map((t) => (
                                <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 font-medium">#{t}</span>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-4 pt-3 border-t border-stone-100 text-xs">
                        <Link
                            to={`/users/${post.user_id}`}
                            data-testid={`post-author-link-${post.id}`}
                            className="flex items-center gap-2 text-stone-600 hover:text-[#990000]"
                        >
                            <div className="w-6 h-6 rounded-full bg-[#990000] text-[#FFCC00] flex items-center justify-center text-[10px] font-bold">
                                {initials}
                            </div>
                            <span className="font-medium">{post.user_name}</span>
                        </Link>
                        <Link to={`/posts/${post.id}`} className="ml-auto inline-flex items-center gap-1.5 text-stone-500 hover:text-[#990000]">
                            <ChatCircle size={14} weight="regular" />
                            <span>{post.comments_count} comments</span>
                        </Link>
                        <AddToCollectionMenu postId={post.id} />
                        <button
                            data-testid={`post-bookmark-btn-${post.id}`}
                            onClick={handleBookmark}
                            disabled={bmBusy}
                            aria-label="Save post"
                            className={`inline-flex items-center gap-1 transition-colors ${
                                post.is_bookmarked ? "text-[#990000]" : "text-stone-400 hover:text-[#990000]"
                            }`}
                        >
                            <BookmarkSimple size={16} weight={post.is_bookmarked ? "fill" : "regular"} />
                        </button>
                    </div>
                </div>
            </div>
        </motion.article>
    );
}
